import { makeAutoObservable } from "mobx";
import { boundarySettingsAPI } from "../API/boundarySettingsAPI";
import { boundarySettingService } from "../services/boundarySettingService";
import commonStore from "../../Store/marco/commonStore";
import dayjs from "dayjs";
import { periodEndFromStart } from "../../EmissionView/utils/reportingPeriod";
import handleAPIError, {
  MESSAGE_CODES,
  showSuccessToast,
} from "../../services/toast";

const service = boundarySettingService;
const API = boundarySettingsAPI;
const cmStore = commonStore;

class BoundarySettingsStore {
  selectedBoundary = 0;
  selectedForm = false;
  selectedYear = null;
  disclosure = 10;
  evaluationNum = 0;
  boundaryViewIsLoading = false;
  boundaryContent = {};
  reportOptionData = [];
  defaultOptionData = [];
  significantData = [];
  isBoundaryEdit = false;
  isReportEdit = false;
  isShowConfirmModal = false;
  isMaterialityEdit = false;
  customAction = null;
  isEditing = false;
  isNew = false; //////// 全新帳號 對應get tigger 新的使用false
  isShowSyncModal = false;
  isShowRemarkModal = false;
  isShowSyncListModal = false;
  isShowWarningModal = false;
  boundaryChanged = false;
  originalBoundaryContent = null;
  questionChanged = false;
  answerChanged = false;
  savedYearOptions = [];
  remarkContent = "";
  syncOriginYear = null;
  isShowClearModal = false;
  isShowNoteModal = false;
  pendingSaveAction = null;
  rmStandardConfirmation = null;
  userChangeSelectedForm = false;
  auditLogData = {
    fullList: [],
    page: 1,
    pageSize: 10,
  };

  //////// loading 參數
  _showDelay = 50;
  _showTimer = null;
  _eaBusy = 0; // 目前有幾個 API 在跑
  _eaShownAt = 0; // loading 顯示的時間點
  _eaHideTimer = null; // 控制延遲收起
  _loadingMin = 350; // 最少顯示 350ms
  _loadingDelay = 500; // 額外緩衝 (避免瞬間關掉不自然)

  constructor() {
    makeAutoObservable(this);
  }

  _handleSetBoundary = (value) => {
    if (this.selectedBoundary !== value) {
      this.selectedBoundary = value;
    }
  };

  _handleSetUserChangeSelectedForm = (value) => {
    this.userChangeSelectedForm = value;
  };

  _handleSetSelectedForm = (value) => {
    this.userChangeSelectedForm = true;
    this.selectedForm = value;
  };

  _handleSetIsEditing = (value) => (this.isEditing = value);

  _handleSetSelectedYear = (year) => {
    if (this.selectedYear !== year) {
      this.rmStandardConfirmation = null;
    }
    this.selectedYear = year;
    //////// 判斷是不是該年份第一次設定且不是該帳號第一次設定
    if (
      this.isNew === false &&
      !this.savedYearOptions.some((item) => item.value === year)
    ) {
      this.isShowSyncModal = true;
    }
  };

  _handleSetBoundaryContent = (obj, isFirst) => {
    // 1. 第一次設定：只記錄原始值，不比較，不標 changed
    if (isFirst === true) {
      this.originalBoundaryContent = { ...obj }; // 🔥 深複製一次
      this.boundaryContent = obj;
      this.boundaryChanged = false;
      this.rmStandardConfirmation = null;
      return;
    }

    // 2. 之後的每一次更新：使用 originalBoundaryContent 做比較
    const prev = this.originalBoundaryContent;
    const curr = obj;

    this.isBoundaryEdit = true;
    cmStore._handleSetStopRoute(true);

    this.boundaryContent = obj;

    // ======= 你的日期邏輯保持不動 =======
    const calculateData = (startDate, baseYear) => {
      if (startDate.format("MM-DD") === "01-01") {
        return `${baseYear}-12-31`;
      }
      return startDate.add(1, "year").subtract(1, "day").format("YYYY-MM-DD");
    };

    const { baseYear, baseStartDate, displayStartDate } = this.boundaryContent;

    if (dayjs(baseStartDate).isValid() && dayjs(displayStartDate).isValid()) {
      const displayNewStartData = dayjs(displayStartDate).year(baseYear);
      this.boundaryContent.displayStartDate =
        displayNewStartData.format("YYYY-MM-DD");

      this.boundaryContent.baseEndDate = periodEndFromStart(baseStartDate);
      this.boundaryContent.displayEndDate = calculateData(
        displayNewStartData,
        baseYear,
      );
    }

    // ======= 變更比較 =======
    const keysToCheck = [
      "standardId",
      "typeId",
      "purposeId",
      "baseYear",
      "displayStartDate",
      "displayEndDate",
      "baseStartDate",
      "baseEndDate",
      "levelOfAssurance",
    ];
    const changed = keysToCheck.some((key) => prev[key] !== curr[key]);

    this.isNew === false
      ? (this.boundaryChanged = changed)
      : (this.boundaryChanged = null);

    console.log("changed = ", this.boundaryChanged);
    console.log("curr = ", JSON.stringify(this.boundaryContent));
  };

  _handleSetBoundaryIsLoading = (value) => {
    if (value === true) {
      this._incLoading();
    } else {
      this._decLoading();
    }
  };

  // BoundarySettingsStore.js
  _incLoading() {
    this._eaBusy += 1;

    // 🌟 關鍵修正：新的 API 開始，立即清除任何正在等待隱藏的計時器
    clearTimeout(this._eaHideTimer);
    this._eaHideTimer = null;

    // 如果 Loading 已經可見 (或延遲顯示已在計時中)，則直接返回
    if (this.boundaryViewIsLoading || this._showTimer) {
      return;
    }

    // 清除之前可能存在的 show timer (理論上上方 if 擋住了，但為安全保留)
    clearTimeout(this._showTimer);

    // 延遲顯示 loading (避免瞬間 API 完成造成 Loading 閃爍)
    this._showTimer = setTimeout(() => {
      // 只有當 _eaBusy > 0 時，才顯示 Loading
      if (this._eaBusy > 0) {
        this.boundaryViewIsLoading = true;
        this._eaShownAt = Date.now(); // 記錄顯示時間點
      }
      this._showTimer = null; // 清理 Timer ID
    }, this._showDelay);
  }

  // BoundarySettingsStore.js

  _decLoading() {
    this._eaBusy = Math.max(0, this._eaBusy - 1);

    // 1. 如果還有 API 在跑，直接返回 (Loading 繼續顯示)
    if (this._eaBusy > 0) return;

    // 2. 如果 API 全部結束，取消任何等待顯示的計時器 (如果還沒顯示)
    clearTimeout(this._showTimer);
    this._showTimer = null;

    // 3. 如果 Loading 不可見 (例如 API 結束得太快，連 showDelay 都沒到)，則直接返回
    if (this.boundaryViewIsLoading === false) return;

    // 4. 計算所需等待時間 (實作最小顯示時間)
    const elapsed = Date.now() - this._eaShownAt;
    const wait = Math.max(this._loadingMin - elapsed, 0); // 至少要顯示 _loadingMin

    // 設置延遲隱藏 Timer (包含計算的 wait 和額外緩衝 _loadingDelay)
    this._eaHideTimer = setTimeout(() => {
      // 最終確認在延遲期間沒有新的 API 開始
      if (this._eaBusy === 0) {
        this.boundaryViewIsLoading = false;
        this._eaShownAt = 0; // 重置時間點
      }
      this._eaHideTimer = null; // 清理 Timer ID
    }, wait + this._loadingDelay); // 🌟 這裡提供了您需要的關閉緩衝
  }
  _handleSetReportOptionData = (arr) => {
    this.reportOptionData = arr;
    console.log(JSON.stringify(this.reportOptionData));
  };
  _handleSetDisclosure = (value) => (this.disclosure = value);
  _handleSetEvaluation = (val) => (this.evaluationNum = val);

  _handleSetSignificantData = (dataKey, data) => {
    this.answerChanged = true;
    this.isMaterialityEdit = true;
    cmStore._handleSetStopRoute(true);
    if (!this.significantData.data) this.significantData.data = {};
    this.significantData.data[dataKey] = data;
    console.log(JSON.stringify(this.significantData.data));
  };

  _handleSetIsShowConfirmModal = (value, selectedPage) => {
    this.isShowConfirmModal = value;
    this.customAction = selectedPage;
  };

  _handleSetBoundaryIsEdit = (val) => {
    this.isBoundaryEdit = val;
    this.customAction = null;
  };

  _handleSetReportIsEdit = (val) => {
    this.isReportEdit = val;
    this.customAction = null;
  };

  _handleSetMaterialityIsEdit = (val) => {
    this.isMaterialityEdit = val;
    this.customAction = null;
  };

  _handleSetIsShowSyncModal = (state) => {
    this.isShowSyncModal = state;
  };

  _handleSetIsShowRemarkModal = (state) => {
    this.isShowRemarkModal = state;
  };

  _handleSetRemarkContent = (content) => {
    this.remarkContent = content;
  };

  _handleSetIsShowSyncListModal = (state) => {
    this.isShowSyncListModal = state;
  };

  _handleSetIsShowWarningModal = (state) => {
    this.isShowWarningModal = state;
  };

  _handleSetSyncOriginYear = (year) => {
    this.syncOriginYear = year;
  };

  _handleSetReportChanged = (state) => {
    this.questionChanged = state;
  };

  _handleSetIsShowClearModal = (state) => {
    this.isShowClearModal = state;
  };

  _handleSetIsShowNoteModal = (state) => {
    this.isShowNoteModal = state;
  };

  _handleSetRMStandardConfirmation = (confirmation) => {
    this.rmStandardConfirmation = confirmation;
  };

  checkHasRemovalMitigationData = async (year) => {
    try {
      const response = await service.hasRemovalMitigationData(year);
      if (!response || response.success === false) {
        handleAPIError(null, { apiData: response, throwError: false });
        return null;
      }

      return response.data === true;
    } catch (err) {
      console.error(err);
      handleAPIError(err, { throwError: false });
      return null;
    }
  };

  _executePendingSaveAction = async () => {
    if (this.pendingSaveAction) {
      await this.pendingSaveAction();
      this.pendingSaveAction = null;
    }
  };

  _handleSetAuditLogData = (key, data) => {
    this.auditLogData[key] = data;
  };

  get auditLogCurrentPageList() {
    const { fullList, page, pageSize } = this.auditLogData;
    return fullList.slice((page - 1) * pageSize, page * pageSize);
  }

  //////// 純內容取得 (原子拆分)
  fetchBoundarySettingContent = async (rootId, userId) => {
    const res = await service.fetchBoundarySettingContent(
      rootId,
      userId,
      1,
      this.selectedYear,
    );
    const standardId = Number(res.data?.standardId);

    this._handleSetBoundaryContent(
      {
        ...res.data,
        standardId: [1, 2].includes(standardId) ? standardId : 2,
        levelOfAssurance:
          res.data?.levelOfAssurance == null
            ? null
            : Number(res.data.levelOfAssurance),
      },
      true,
    );
  };

  //////// 儲存邊界設定資料 (原子拆分)
  fetchSavedBS = async (userId) => {
    return service.saveBS(
      this.boundaryContent,
      userId,
      this.selectedYear,
      this.selectedForm,
      this.remarkContent,
    );
  };

  //////// 取得已設定年份 (原子拆分)
  fetchSavedYearOptions = async (userId) => {
    const res = await service.fetchSetYears(userId);
    this.savedYearOptions = res ?? [];
    this.isNew = this.savedYearOptions.length === 0;
  };

  //////// 取得報告邊界的問題資料 (原子拆分)
  fetchRBData = async (userId) => {
    const res = await service.fetchRBData(
      userId,
      this.selectedYear,
      this.selectedForm,
      this.boundaryContent.standardId,
    );

    const data = res.data || {};
    this.reportOptionData = data?.significantOptions || [];
    this.defaultOptionData = data?.significantOptions || [];
    this.disclosure = data?.threshold || 0;
    this.evaluationNum = data?.maxEvaluationNum || 0;
  };

  //////// 取得重大性評估的答案資料
  fetchMaterialityData = async (userId) => {
    const res = await service.fetchMaterialityData(
      userId,
      this.selectedYear,
      this.selectedForm,
      this.boundaryContent.standardId,
    );

    this.significantData = res ?? {};
  };

  //////// 取得組織邊界初始內容
  getBSInitial = async (rootId, userId) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      //////// 已經有設定的年份
      await this.fetchSavedYearOptions(userId);

      //////// 自動預選年份
      if (!this.selectedYear) {
        this.selectedYear = this.savedYearOptions[0]?.value ?? null;
      }

      //////// 上次儲存的內容
      await this.fetchBoundarySettingContent(rootId, userId);
    } catch (err) {
      this.boundaryContent = {};
      console.error(err);
      return null; //////// 不要破壞畫面
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 儲存組織邊界
  handleSaveBS = async (rootId, userId, force = false) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      const hasConfirmedRMStandardChange =
        Number(this.rmStandardConfirmation?.year) ===
          Number(this.selectedYear) &&
        Number(this.rmStandardConfirmation?.standardId) ===
          Number(this.boundaryContent.standardId);

      if (!force && !hasConfirmedRMStandardChange) {
        let baseStandardId = null;
        try {
          baseStandardId = await service.getStandardIdByYear(
            userId,
            this.boundaryContent.baseYear,
          );
        } catch (err) {
          console.error(err);
          baseStandardId = null;
        }

        if (!baseStandardId) {
          this.handleSaveBS(rootId, userId, true);
          return false;
        }

        if (baseStandardId !== this.boundaryContent.standardId) {
          this._handleSetIsShowNoteModal(true);
          this.pendingSaveAction = () =>
            this.handleSaveBS(rootId, userId, true);
          return false; // Interrupted
        }
      }

      const response = await this.fetchSavedBS(userId);
      await this.fetchSavedYearOptions(userId);
      await this.fetchBoundarySettingContent(rootId, userId);
      this.remarkContent = "";
      this._handleSetBoundaryIsEdit(false);
      showSuccessToast({
        apiData: response,
        scenarioCode: MESSAGE_CODES.BOUNDARY_ORG_SAVE_SUCCESS,
      });
      return true; // Success
    } catch (err) {
      console.error(err);
      handleAPIError(err, { throwError: false });
      return false; // Failed
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 同步選定年份的組織邊界資料
  handleSyncBS = async (rootId, userId) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      const response = await service.syncBS(
        rootId,
        userId,
        this.syncOriginYear,
        this.selectedYear,
      );

      await this.fetchSavedYearOptions(userId);
      await this.fetchBoundarySettingContent(rootId, userId);
      showSuccessToast({
        apiData: response,
        scenarioCode: MESSAGE_CODES.BOUNDARY_ORG_SAVE_SUCCESS,
      });
    } catch (err) {
      console.error(err);
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 刪除該年份的組織邊界
  handleDeleteBS = async (rootId, userId) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      const response = await service.deleteBS(rootId, this.selectedYear);
      await this.fetchSavedYearOptions(userId);
      await this.fetchBoundarySettingContent(rootId, userId);
      showSuccessToast({
        apiData: response,
        scenarioCode: MESSAGE_CODES.BOUNDARY_ORG_DELETE_SUCCESS,
      });
    } catch (err) {
      console.error(err);
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 取得報告邊界初始內容
  getRBInitial = async (rootId, userId) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      //////// 已經有設定的年份
      await this.fetchSavedYearOptions(userId);

      //////// 判斷目前選擇的年份是不是在已經設定過的年份內
      if (
        !this.savedYearOptions?.some((item) => item.value === this.selectedYear)
      ) {
        this.selectedYear = this.savedYearOptions[0]?.value ?? null;
      }

      //////// 取得儲存的內容
      await this.fetchBoundarySettingContent(rootId, userId);
      this.selectedForm = this.boundaryContent.isCustom || false;
      await this.fetchRBData(userId);
      //////// 取得報告邊界的問題
    } catch (err) {
      this.boundaryContent = {};
      this.selectedForm = false;
      console.error(err);
      return null; //////// 不要破壞畫面
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 儲存報告邊界
  handleSaveRB = async (rootId, userId) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      const requestBody = {
        threshold: this.disclosure,
        significantOptions: this.selectedForm
          ? this.reportOptionData
          : this.defaultOptionData,
        remark: this.remarkContent,
      };

      const response = await service.saveRB(
        userId,
        this.selectedYear,
        this.selectedForm,
        this.boundaryContent.standardId,
        requestBody,
      );

      this.remarkContent = "";
      this._handleSetReportIsEdit(false);
      showSuccessToast({
        apiData: response,
        scenarioCode: MESSAGE_CODES.BOUNDARY_REPORTING_SAVE_SUCCESS,
      });

      await this.fetchSavedYearOptions(userId);
      await this.fetchBoundarySettingContent(rootId, userId);
      this.selectedForm = this.boundaryContent.isCustom || false;
      await this.fetchRBData(userId);
    } catch (err) {
      this.boundaryContent = {};
      this.selectedForm = false;
      console.error(err);
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 取得重大性評估的答案資料
  getMaterialityInitial = async (rootId, userId) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      //////// 已經有設定的年份
      await this.fetchSavedYearOptions(userId);
      //////// 判斷目前選擇的年份是不是在已經設定過的年份內
      if (
        !this.savedYearOptions?.some((item) => item.value === this.selectedYear)
      ) {
        this.selectedYear = this.savedYearOptions[0]?.value ?? null;
      }

      //////// 取得儲存的內容
      await this.fetchBoundarySettingContent(rootId, userId);
      this.selectedForm = this.boundaryContent.isCustom || false;
      await this.fetchMaterialityData(userId);
    } catch (err) {
      this.boundaryContent = {};
      this.selectedForm = false;
      this.significantData = {};
      console.error(err);
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 儲存重大性評估資料
  handleSaveMaterility = async (rootId, userId) => {
    this._handleSetBoundaryIsLoading(true);
    try {
      const response = await service.saveMaterialityData(
        userId,
        this.selectedYear,
        this.selectedForm,
        this.boundaryContent.standardId,
        this.remarkContent,
        this.significantData.data,
      );
      showSuccessToast({
        apiData: response,
        scenarioCode: MESSAGE_CODES.BOUNDARY_CRITERIA_SAVE_SUCCESS,
      });
      this.remarkContent = "";
      this._handleSetMaterialityIsEdit(false);

      await this.fetchSavedYearOptions(userId);
      await this.fetchBoundarySettingContent(rootId, userId);
      this.selectedForm = this.boundaryContent.isCustom || false;
      await this.fetchMaterialityData(userId);
    } catch (err) {
      console.error(err);
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };

  //////// 取得稽核紀錄清單
  getLoginitial = async (userId) => {
    this._handleSetBoundaryIsLoading(true);
    this.auditLogData.page = 1;
    this.auditLogData.fullList = [];
    try {
      await this.fetchSavedYearOptions(userId);
      const data = await service.fetchLog(userId, this.selectedYear);
      if (data.length > 0) this.auditLogData.fullList = data;
    } catch (err) {
      this.auditLogData.fullList = [];
      console.error(err);
    } finally {
      this._handleSetBoundaryIsLoading(false);
    }
  };
}

const boundarySettingsStore = new BoundarySettingsStore();
export default boundarySettingsStore;
